// ---------------------------------------------------------------------------
// useGraders — Fetch and manage grader state for a specific workspace.
//
// When `workspaceName` is null the hook stays idle and returns an empty,
// non-loading result so the consumer can render a "pick a workspace"
// placeholder without firing a network call. Mirrors the
// useEvaluationTemplates pattern.
// ---------------------------------------------------------------------------

import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';

import { AppError } from '../../api/errors';
import { useHttpClient } from '../../api/HttpClientContext';
import { WorkflowApiService } from '../../services/workflowApi';

import type { Grader } from './useGraders.interface';
import { mapServerGrader, sortGradersByNewest } from './useGraders.utils';

export type { Grader } from './useGraders.interface';

export interface UseGradersParams {
  /**
   * Workspace whose graders to fetch. When `null` the hook returns an
   * empty, non-loading result and skips the network call.
   */
  workspaceName: string | null;
}

export interface UseGradersReturn {
  graders: Grader[];
  setGraders: Dispatch<SetStateAction<Grader[]>>;
  loading: boolean;
  error: AppError | null;
}

export function useGraders(params: UseGradersParams): UseGradersReturn {
  const { workspaceName } = params;
  const client = useHttpClient();
  const [graders, setGraders] = useState<Grader[]>([]);
  const [loading, setLoading] = useState(workspaceName !== null);
  const [error, setError] = useState<AppError | null>(null);

  useEffect(() => {
    if (workspaceName === null) {
      setGraders([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const service = new WorkflowApiService(client);

    service
      .listGraders(workspaceName)
      .then((serverGraders) => {
        if (cancelled) return;
        // Sort once at the source (newest first) so every consumer —
        // GraderContext's initial selection and GraderList's rendering —
        // sees the same display order without re-sorting.
        setGraders(sortGradersByNewest(serverGraders.map(mapServerGrader)));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof AppError ? err : new AppError(String(err), 'UNKNOWN'),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [client, workspaceName]);

  return { graders, setGraders, loading, error };
}
